import React, { useEffect, useState, useCallback } from "react";
import HPageContainer from "src/hcomponents/HPageContainer";
import HHeader from "src/hcomponents/HHeader";
import { useHistory } from "react-router-dom";
import spaceManagementService from "src/services/host-apiServices/spaceManagementService";
import apiService from "src/services/apiService";
import { getUser } from "src/services/localService";
import { Building2, Clock, Calendar, Plus, Megaphone, MapPin, DollarSign } from "lucide-react";
import { convertToUTC, getCurrentTimezoneOffset } from "../../../utils/timezoneUtils";
import { canModify } from "../../../utils/bookingActionsUtils";
import { getEventName } from "../../../utils/spaceNamingUtils";
import HSpaceDetailsModal from "../../../hcomponents/HSpaceDetailsModal";
import { useSubscription } from "../../../hooks/useSubscription";
import ReusableBookingModal from "../../../components/ReusableBookingModal";
import useBookingWebSocket from "../../../hooks/useBookingWebSocket";
import useNotificationWebSocket from "../../../hooks/useNotificationWebSocket";
import BookingDetailsModal from "./BookingDetailsModal";
import moment from "moment";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import { DatePicker } from "../../../shadcn/ui/date-time-picker";
import { Button } from "../../../shadcn/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../shadcn/ui/popover";
import { CalendarIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../shadcn/ui/dialog";
import logger from "../../../utils/logger";


const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const UserDashboard = () => {
  const user = getUser();
  const history = useHistory();
  const [todayBooking, setTodayBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [recentBookings, setRecentBookings] = useState([]);
  const [announcementImgErrors, setAnnouncementImgErrors] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [selectedSpace, setSelectedSpace] = useState(null);

  const [isAllAnnouncementsModalOpen, setIsAllAnnouncementsModalOpen] = useState(false);
  const [allAnnouncements, setAllAnnouncements] = useState([]);
  const [allAnnouncementsPage, setAllAnnouncementsPage] = useState(0);
  const [allAnnouncementsLoading, setAllAnnouncementsLoading] = useState(false);
  const [hasMoreAnnouncements, setHasMoreAnnouncements] = useState(true);

  // WebSocket for real-time booking updates
  useBookingWebSocket(update => {
    if (todayBooking && String(todayBooking.id) === String(update.bookingId)) {
      setTodayBooking(prev => ({ ...prev, status: update.newStatus }));
    }
    setRecentBookings(prev =>
      prev.map(b => (String(b.id) === String(update.bookingId) ? { ...b, status: update.newStatus } : b))
    );
  });

  // Helper to parse date from various formats (Java date object, ISO string, or timestamp)
  const parseNotificationDate = useCallback(dateValue => {
    if (!dateValue) return new Date();

    // Handle Java date object format
    if (dateValue.year !== undefined && dateValue.monthValue !== undefined) {
      return new Date(
        dateValue.year,
        dateValue.monthValue - 1, // JS months are 0-indexed
        dateValue.dayOfMonth || 1,
        dateValue.hour || 0,
        dateValue.minute || 0,
        dateValue.second || 0
      );
    }

    // Handle ISO string or timestamp
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }, []);

  // Helper to format a notification from WebSocket or REST API payload
  const formatNotification = useCallback(
    notification => ({
      title: notification.title || "Announcement",
      priority: (notification.priority || "medium").toLowerCase(),
      image: notification.imageUrls && notification.imageUrls.length > 0 ? notification.imageUrls[0] : null,
      message: notification.body || notification.message || "No message available",
      date: notification.createdAt
        ? moment(parseNotificationDate(notification.createdAt)).format("MM/DD/YYYY")
        : new Date().toLocaleDateString(),
    }),
    [parseNotificationDate]
  );

  // WebSocket for real-time notifications
  useNotificationWebSocket(
    // Callback for individual real-time notifications
    useCallback(
      notification => {
        logger.log("🔔 Real-time announcement received:", notification);
        const formattedNotification = formatNotification(notification);
        setAnnouncements(prev => {
          // Check if announcement already exists (by title to avoid duplicates)
          if (prev.some(a => a.title === notification.title)) {
            return prev;
          }
          return [formattedNotification, ...prev.slice(0, 4)]; // Keep only 5 announcements
        });
      },
      [formatNotification]
    ),
    // Callback for batch notifications (on connect/reconnect)
    useCallback(
      ({ notifications: newNotifications }) => {
        logger.log(`📬 Received ${newNotifications.length} announcements for host-customer-dashboard`);
        const formattedAnnouncements = newNotifications.slice(0, 5).map(formatNotification);
        setAnnouncements(formattedAnnouncements);
        setAnnouncementsLoading(false);
      },
      [formatNotification]
    )
  );
  const [isSpaceModalOpen, setIsSpaceModalOpen] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingModalMode, setBookingModalMode] = useState("view");
  const [isBookingDetailsModalOpen, setIsBookingDetailsModalOpen] = useState(false);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);
  const { hasActiveSubscription } = useSubscription();
  const [selectedTimeRange, setSelectedTimeRange] = useState("Last 30 Days");
  const [customStartDate, setCustomStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [customEndDate, setCustomEndDate] = useState(new Date());

  // Helper to get date range from time range string
  const getDateRange = timeRange => {
    const today = new Date();
    const endDate = new Date(today);
    let startDate = new Date(today);

    if (timeRange === "Custom Range") {
      return {
        startDate: customStartDate.toISOString().split("T")[0],
        endDate: customEndDate.toISOString().split("T")[0],
      };
    }

    switch (timeRange) {
      case "Last 7 Days":
        startDate.setDate(today.getDate() - 7);
        break;
      case "Last 30 Days":
        startDate.setDate(today.getDate() - 30);
        break;
      case "Last 90 Days":
        startDate.setDate(today.getDate() - 90);
        break;
      case "This Month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "Last Month":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate.setDate(0); // Last day of previous month
        break;
      default:
        startDate.setDate(today.getDate() - 30);
    }

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  useEffect(() => {
    const fetchTodayBookings = async () => {
      setLoading(true);
      setError(null);
      try {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const dateStr = `${yyyy}-${mm}-${dd}`;
        const user = getUser();
        const userId = user?.id;

        if (!userId) {
          setError("Authentication required. Please log in.");
          setLoading(false);
          return;
        }
        // Fetch today's bookings
        const filters = {
          userId,
          startTimeFrom: convertToUTC(new Date(dateStr + "T00:00:00")),
          startTimeTo: convertToUTC(new Date(dateStr + "T23:59:59")),
          sortBy: "startTime",
          sortOrder: "asc",
        };
        const timezone = getCurrentTimezoneOffset();
        const res = await spaceManagementService.searchBookings(filters, timezone);
        const bookings = res.data?.content || res.data?.data || [];
        setTodayBooking(bookings[0] || null);
        // Fetch recent bookings (last 3, sorted desc)
        const recentRes = await spaceManagementService.searchBookings(
          {
            userId,
            pageSize: 3,
            sortBy: "startTime",
            sortOrder: "desc",
          },
          timezone
        );
        setRecentBookings(recentRes.data?.content || recentRes.data?.data || []);
      } catch (err) {
        setError("Failed to load bookings");
      } finally {
        setLoading(false);
      }
    };

    const fetchAnnouncements = async () => {
      setAnnouncementsLoading(true);
      try {
        const user = getUser();
        const userId = user?.id;
        if (!userId) return;

        const response = await apiService.getUserAnnouncements(0, 5, userId);

        if (response.data) {
          const privateNotifs = response.data.privateNotifications || [];
          const publicNotifs = response.data.publicNotifications || [];
          const data = [...privateNotifs, ...publicNotifs];

          const mapped = data
            .map(item => ({
              ...item,
              _sortDate: parseNotificationDate(item.createdAt),
            }))
            .sort((a, b) => b._sortDate - a._sortDate)
            .map(item => {
              // Parse complex Java date object
              let formattedDate = new Date().toLocaleDateString();
              if (item.createdAt) {
                try {
                  if (typeof item.createdAt === "object" && item.createdAt.year) {
                    const dateObj = new Date(
                      item.createdAt.year,
                      item.createdAt.monthValue - 1,
                      item.createdAt.dayOfMonth,
                      item.createdAt.hour,
                      item.createdAt.minute,
                      item.createdAt.second
                    );
                    formattedDate = dateObj.toLocaleDateString();
                  } else {
                    formattedDate = new Date(item.createdAt).toLocaleDateString();
                  }
                } catch (e) {
                  logger.error("Error parsing date:", e);
                }
              }

              return {
                title: item.title || "Announcement",
                priority: (item.priority || "medium").toLowerCase(),
                image: item.imageUrls && item.imageUrls.length > 0 ? item.imageUrls[0] : null,
                message: item.body || item.message || "No message available",
                date: formattedDate,
              };
            });
          setAnnouncements(mapped.slice(0, 5));
        }
      } catch (err) {
        logger.error("Error fetching announcements:", err);
      } finally {
        setAnnouncementsLoading(false);
      }
    };

    fetchAnnouncements();
    fetchTodayBookings();
  }, []);

  const fetchAllAnnouncements = useCallback(async (page = 0) => {
    setAllAnnouncementsLoading(true);
    try {
      const user = getUser();
      const userId = user?.id;
      if (!userId) return;

      const response = await apiService.getUserAnnouncements(page, 10, userId);

      if (response.data) {
        const privateNotifs = response.data.privateNotifications || [];
        const publicNotifs = response.data.publicNotifications || [];
        const data = [...privateNotifs, ...publicNotifs];

        const mapped = data
          .map(item => ({
            ...item,
            _sortDate: parseNotificationDate(item.createdAt),
          }))
          .sort((a, b) => b._sortDate - a._sortDate)
          .map(item => {
            let formattedDate = new Date().toLocaleDateString();
            if (item.createdAt) {
              try {
                if (typeof item.createdAt === "object" && item.createdAt.year) {
                  const dateObj = new Date(
                    item.createdAt.year,
                    item.createdAt.monthValue - 1,
                    item.createdAt.dayOfMonth,
                    item.createdAt.hour,
                    item.createdAt.minute,
                    item.createdAt.second
                  );
                  formattedDate = dateObj.toLocaleDateString();
                } else {
                  formattedDate = new Date(item.createdAt).toLocaleDateString();
                }
              } catch (e) {
                logger.error("Error parsing date:", e);
              }
            }

            return {
              title: item.title || "Announcement",
              priority: (item.priority || "medium").toLowerCase(),
              image: item.imageUrls && item.imageUrls.length > 0 ? item.imageUrls[0] : null,
              message: item.body || item.message || "No message available",
              date: formattedDate,
            };
          });

        if (page === 0) {
          setAllAnnouncements(mapped);
        } else {
          setAllAnnouncements(prev => [...prev, ...mapped]);
        }
        setHasMoreAnnouncements(data.length === 10);
        setAllAnnouncementsPage(page);
      }
    } catch (err) {
      logger.error("Error fetching all announcements:", err);
    } finally {
      setAllAnnouncementsLoading(false);
    }
  }, []);

  const handleViewAllAnnouncements = () => {
    setIsAllAnnouncementsModalOpen(true);
    if (allAnnouncements.length === 0) {
      fetchAllAnnouncements(0);
    }
  };

  // Helper to format time range and duration
  const getTimeRange = (start, end) => {
    if (!start || !end) return "";
    const startTime = start.slice(11, 16);
    const endTime = end.slice(11, 16);
    return `${startTime} - ${endTime}`;
  };
  const getDuration = (start, end) => {
    if (!start || !end) return "";
    const s = new Date(start);
    const e = new Date(end);
    const diffMs = e - s;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs / (1000 * 60)) % 60);
    return hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""}${mins ? ` ${mins} min` : ""}` : `${mins} min`;
  };
  const getDateString = iso => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  // intentionally no getTimeString helper; not needed currently

  // Badge color for announcement priority
  const badgeColor = priority =>
    priority === "high"
      ? "bg-pink-600 text-white"
      : priority === "medium"
        ? "bg-blue-600 text-white"
        : "bg-gray-300 text-gray-800";

  // Handle viewing/modifying booking
  const handleViewBooking = booking => {
    setSelectedBooking(booking);
    setBookingModalMode("view");
    setShowBookingModal(true);
  };

  const handleModifyBooking = booking => {
    setSelectedBooking(booking);
    setBookingModalMode("modify");
    setShowBookingModal(true);
  };

  const handleCloseBookingModal = () => {
    setShowBookingModal(false);
    setSelectedBooking(null);
  };

  // Handle opening booking details modal
  const handleViewSpaceDetails = booking => {
    setSelectedBookingDetails(booking);
    setIsBookingDetailsModalOpen(true);
  };

  const handleCloseSpaceModal = () => {
    setIsSpaceModalOpen(false);
    setSelectedSpace(null);
  };

  const handleCreateBooking = async bookingData => {
    logger.log("Creating booking:", bookingData);
  };

  const handleCreateBookingPendingPayment = async bookingData => {
    logger.log("Creating pending payment booking:", bookingData);
  };

  return (
    <HPageContainer>
      <HHeader
        rightButton={
          <div className="flex items-center gap-3">
            {selectedTimeRange === "Custom Range" && (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4 duration-300">
                <DatePicker
                  date={customStartDate}
                  setDate={setCustomStartDate}
                  placeholder="Start"
                  className="w-[130px]"
                />
                <span className="text-gray-400 text-xs">to</span>
                <DatePicker date={customEndDate} setDate={setCustomEndDate} placeholder="End" className="w-[130px]" />
              </div>
            )}
            <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
              <SelectTrigger className="w-[150px] bg-white border-gray-200">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Last 7 Days">Last 7 Days</SelectItem>
                <SelectItem value="Last 30 Days">Last 30 Days</SelectItem>
                <SelectItem value="Last 90 Days">Last 90 Days</SelectItem>
                <SelectItem value="This Month">This Month</SelectItem>
                <SelectItem value="Last Month">Last Month</SelectItem>
                <SelectItem value="Custom Range">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />
      <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8">
        {/* Top Greeting Banner */}
        <div className="mx-auto mb-6 max-w-screen-2xl">
          <div className="flex flex-col gap-2 px-6 py-6 bg-blue-100 rounded-xl sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-2 items-center">
              <span className="text-2xl font-bold text-gray-900">
                {getGreeting()}, {user?.firstName || "there"}!{" "}
                <span role="img" aria-label="wave">
                  👋
                </span>
              </span>
            </div>
            <div className="text-base text-gray-500">
              Welcome back to your workspace dashboard. Here's what's happening today.
            </div>
          </div>
        </div>
        {/* Main Grid */}
        <div className="grid grid-cols-1 gap-8 mx-auto max-w-screen-2xl lg:grid-cols-3">
          {/* Left/Main Column */}
          <div className="flex flex-col gap-6 lg:col-span-2">
            {/* Today's Booking Card */}
            <div className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center mb-2">
                <Clock className="mr-2 w-5 h-5 text-gray-500" />
                <span className="text-lg font-semibold text-gray-900">Today's Booking</span>
              </div>
              <div className="mb-4 text-gray-500">Your upcoming reservation for today</div>
              {loading ? (
                <div className="text-gray-400">Loading...</div>
              ) : error ? (
                <div className="text-red-500">{error}</div>
              ) : todayBooking ? (
                <div className="flex gap-4 items-start">
                  {imgError || !todayBooking.media?.images?.[0] ? (
                    <div className="flex justify-center items-center flex-shrink-0 w-20 h-20 bg-blue-50 rounded-lg">
                      <Building2 className="w-10 h-10 text-blue-400" />
                    </div>
                  ) : (
                    <img
                      src={todayBooking.media.images[0]}
                      className="object-cover flex-shrink-0 w-20 h-20 rounded-lg"
                      alt="Room"
                      onError={() => setImgError(true)}
                    />
                  )}
                  <div className="flex flex-col flex-1 gap-2">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {todayBooking.spaceInstance?.title || getEventName(todayBooking.spaceInstance) || "Space"}
                      </div>
                      {todayBooking.spaceInstance?.location?.buildingAddress && (
                        <div className="flex items-center gap-1 mt-1 text-sm text-gray-600">
                          <MapPin className="w-3.5 h-3.5" />
                          <span>
                            {[
                              todayBooking.spaceInstance.location.buildingAddress.streetAddress,
                              todayBooking.spaceInstance.location.buildingAddress.city,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm">
                      <span className="inline-flex items-center gap-1 border border-blue-200 bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                        <Clock className="w-3.5 h-3.5" />
                        {getTimeRange(todayBooking.startTime, todayBooking.endTime)}
                      </span>
                      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                        {getDuration(todayBooking.startTime, todayBooking.endTime)}
                      </span>
                    </div>
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => handleViewSpaceDetails(todayBooking)}
                        className="px-4 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        View Details
                      </button>
                      {canModify(todayBooking) && (
                        <button
                          onClick={() => handleModifyBooking(todayBooking)}
                          className="px-4 py-1.5 text-sm font-medium bg-white rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                          Modify
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-400">No bookings for today.</div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#4338ca] text-white font-semibold text-base shadow-sm hover:bg-[#3730a3] transition-colors"
                onClick={() => history.push("/host/book-space")}
              >
                <Plus className="w-5 h-5" /> Book New Space
              </button>
              <button
                className="flex flex-1 gap-2 justify-center items-center px-4 py-3 text-base font-semibold text-gray-900 bg-white rounded-xl border border-gray-200 transition-colors hover:bg-gray-50"
                onClick={() => history.push("/host/my-bookings")}
              >
                <Calendar className="w-5 h-5" /> Upcoming Bookings
              </button>
            </div>

            {/* Recent Activity */}
            <div className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="mb-1 text-lg font-semibold text-gray-900">Recent Activity</div>
              <div className="mb-4 text-sm text-gray-500">Your last 3 bookings</div>
              <div className="divide-y divide-gray-100">
                {recentBookings.length === 0 ? (
                  <div className="py-6 text-center text-gray-400">No recent bookings.</div>
                ) : (
                  recentBookings.map(booking => {
                    const imgErr = booking.imgError;
                    return (
                      <div
                        key={booking.id}
                        className="flex gap-4 items-center py-4 first:pt-0 last:pb-0 cursor-pointer hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors"
                        onClick={() => handleViewSpaceDetails(booking)}
                      >
                        {imgErr || !booking.media?.images?.[0] ? (
                          <div className="flex justify-center items-center flex-shrink-0 w-14 h-14 bg-blue-50 rounded-lg">
                            <Building2 className="w-7 h-7 text-blue-400" />
                          </div>
                        ) : (
                          <img
                            src={booking.media.images[0]}
                            className="object-cover flex-shrink-0 w-14 h-14 rounded-lg"
                            alt="Room"
                            onError={() => {
                              booking.imgError = true;
                              setRecentBookings([...recentBookings]);
                            }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">
                            {booking.spaceInstance?.title || getEventName(booking.spaceInstance) || "Space"}
                          </div>
                          <div className="text-sm text-gray-600 truncate">
                            {getDateString(booking.startTime)} • {getTimeRange(booking.startTime, booking.endTime)}
                          </div>
                          {booking.spaceInstance?.location?.buildingAddress && (
                            <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate">{booking.spaceInstance.location.buildingAddress.city}</span>
                            </div>
                          )}
                        </div>
                        <span className="flex-shrink-0 px-3 py-1 text-xs font-semibold text-gray-700 bg-gray-100 rounded-full">
                          Completed
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="flex flex-col gap-6 lg:col-span-1">
            <div className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex gap-2 items-center mb-2">
                <Megaphone className="w-5 h-5 text-blue-600" />
                <span className="text-base font-semibold text-gray-900">Announcements from Admin</span>
              </div>
              <div className="mb-4 text-sm text-gray-500">Important updates and notices</div>
              <div className="flex flex-col gap-4">
                {announcementsLoading ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                    <div className="text-sm">Loading announcements...</div>
                  </div>
                ) : announcements.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center text-gray-500">
                    <div className="flex justify-center items-center mb-3 w-14 h-14 bg-blue-50 rounded-full">
                      <Megaphone className="w-7 h-7 text-blue-400" />
                    </div>
                    <div className="text-sm">No announcements at the moment</div>
                  </div>
                ) : (
                  announcements.map((a, idx) => {
                    const isLast = idx === announcements.length - 1;
                    const hideImg = announcementImgErrors[idx];
                    return (
                      <div key={idx} className={`bg-white ${!isLast ? "pb-4 border-b border-gray-100" : ""}`}>
                        <div className="flex justify-between items-center mb-1">
                          <h3 className="font-medium text-gray-900">{a.title}</h3>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-lg font-semibold ml-2 ${badgeColor(a.priority)}`}
                          >
                            {a.priority}
                          </span>
                        </div>
                        {a.image && !hideImg ? (
                          <img
                            src={a.image}
                            alt={a.title}
                            className="object-cover my-2 w-full h-28 rounded-lg"
                            onError={() =>
                              setAnnouncementImgErrors(prev => ({
                                ...prev,
                                [idx]: true,
                              }))
                            }
                          />
                        ) : (
                          <div className="flex justify-center items-center my-2 w-full h-28 bg-blue-50 rounded-lg">
                            <Megaphone className="w-8 h-8 text-blue-400" />
                          </div>
                        )}
                        <p className="text-sm text-gray-600">{a.message}</p>
                        <p className="mt-2 text-xs text-gray-400">{a.date}</p>
                      </div>
                    );
                  })
                )}
                {announcements.length > 0 && (
                  <button
                    onClick={handleViewAllAnnouncements}
                    className="mt-2 w-full text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors text-center py-2 bg-blue-50 rounded-lg"
                  >
                    View All Announcements
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedBooking && (
        <ReusableBookingModal
          isOpen={showBookingModal}
          onClose={handleCloseBookingModal}
          booking={selectedBooking}
          mode={bookingModalMode}
          closeOnSuccess={true}
        />
      )}

      {/* Space Details Modal */}
      {isSpaceModalOpen && selectedSpace && (
        <HSpaceDetailsModal
          isOpen={isSpaceModalOpen}
          onClose={handleCloseSpaceModal}
          space={selectedSpace}
          selectedDate={new Date()}
          availableTimeSlots={[]}
          onCreateBooking={handleCreateBooking}
          onCreateBookingPendingPayment={handleCreateBookingPendingPayment}
          userCurrency="KES"
          hasActiveSubscription={hasActiveSubscription}
          bookingError={false}
          myGroups={[]}
          timeFormat="12h"
          viewOnly={false}
        />
      )}

      {/* Booking Details Modal */}
      <BookingDetailsModal
        isOpen={isBookingDetailsModalOpen}
        onClose={() => setIsBookingDetailsModalOpen(false)}
        booking={selectedBookingDetails}
      />

      {/* All Announcements Modal */}
      <Dialog open={isAllAnnouncementsModalOpen} onOpenChange={setIsAllAnnouncementsModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b border-gray-100">
            <DialogTitle>All Announcements</DialogTitle>
          </DialogHeader>
          <div
            className="flex-1 overflow-y-auto px-6 py-4"
            onScroll={e => {
              const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 10;
              if (bottom && hasMoreAnnouncements && !allAnnouncementsLoading) {
                fetchAllAnnouncements(allAnnouncementsPage + 1);
              }
            }}
          >
            <div className="flex flex-col gap-4">
              {allAnnouncements.map((a, idx) => {
                const isLast = idx === allAnnouncements.length - 1;
                return (
                  <div key={idx} className={`bg-white ${!isLast ? "pb-4 border-b border-gray-100" : ""}`}>
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="font-medium text-gray-900">{a.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-lg font-semibold ml-2 ${badgeColor(a.priority)}`}>
                        {a.priority}
                      </span>
                    </div>
                    {a.image ? (
                      <img src={a.image} alt={a.title} className="object-cover my-2 w-full h-40 rounded-lg" />
                    ) : (
                      <div className="flex justify-center items-center my-2 w-full h-28 bg-blue-50 rounded-lg">
                        <Megaphone className="w-8 h-8 text-blue-400" />
                      </div>
                    )}
                    <p className="text-sm text-gray-600">{a.message}</p>
                    <p className="mt-2 text-xs text-gray-400">{a.date}</p>
                  </div>
                );
              })}
              {allAnnouncementsLoading && (
                <div className="py-4 text-center text-sm text-gray-500">
                  <div className="animate-spin inline-block rounded-full h-5 w-5 border-b-2 border-blue-600 mb-1"></div>
                  <div>Loading...</div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </HPageContainer>
  );
};

export default UserDashboard;
